/**
 * Radar domain schemas using Zod for validation
 * Implements the schema validation approach from ADR-018
 */
import { z } from 'zod';

// Basic data types
export const QuadrantSchema = z.enum([
  'tools',
  'techniques',
  'platforms',
  'languages',
]);
export type Quadrant = z.infer<typeof QuadrantSchema>;

export const RingSchema = z.enum(['adopt', 'trial', 'assess', 'hold']);
export type Ring = z.infer<typeof RingSchema>;

export const MovementSchema = z.enum(['up', 'down', 'none']);
export type Movement = z.infer<typeof MovementSchema>;

// Radar item schema
export const RadarItemSchema = z.object({
  id: z.string().uuid().optional(),
  name: z
    .string()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or less'),
  quadrant: QuadrantSchema,
  ring: RingSchema,
  description: z
    .string()
    .max(500, 'Description must be 500 characters or less'),
  isNew: z.boolean().default(false),
  moved: MovementSchema.default('none'),
});
export type RadarItem = z.infer<typeof RadarItemSchema>;

// Quadrant schema
export const QuadrantDefinitionSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().min(1, 'Quadrant name is required'),
  items: z.array(RadarItemSchema).default([]),
});
export type QuadrantDefinition = z.infer<typeof QuadrantDefinitionSchema>;

// Radar schema
export const RadarSchema = z.object({
  id: z.string().uuid().optional(),
  tenantId: z.string().min(1, 'Tenant ID is required'),
  teamId: z.string().min(1, 'Team ID is required'),
  name: z
    .string()
    .min(1, 'Radar name is required')
    .max(100, 'Radar name must be 100 characters or less'),
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or less')
    .optional(),
  quadrants: z
    .array(QuadrantDefinitionSchema)
    .min(1, 'At least one quadrant is required'),
  createdAt: z.date().default(() => new Date()),
  updatedAt: z.date().default(() => new Date()),
  ownerId: z.string().min(1, 'Owner ID is required'),
});
export type Radar = z.infer<typeof RadarSchema>;

/**
 * Validate radar data with detailed error reporting
 * @param data Unknown data to validate against radar schema
 * @returns Validated radar data or throws ValidationError
 */
export function validateRadar(data: unknown): Radar {
  const result = RadarSchema.safeParse(data);

  if (!result.success) {
    const formattedErrors = result.error.errors.map(
      (err: z.ZodIssue) => `${err.path.join('.')}: ${err.message}`
    );

    // Integration with existing error system
    throw new Error(`Radar validation failed: ${formattedErrors.join(', ')}`);
  }

  return result.data;
}

/**
 * Validate radar item data with detailed error reporting
 * @param data Unknown data to validate against radar item schema
 * @returns Validated radar item data or throws ValidationError
 */
export function validateRadarItem(data: unknown): RadarItem {
  const result = RadarItemSchema.safeParse(data);

  if (!result.success) {
    const formattedErrors = result.error.errors.map(
      (err: z.ZodIssue) => `${err.path.join('.')}: ${err.message}`
    );

    // Integration with existing error system
    throw new Error(
      `Radar item validation failed: ${formattedErrors.join(', ')}`
    );
  }

  return result.data;
}
